import {
	Component,
	OnInit,
	OnDestroy,
	AfterViewInit,
	HostListener,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import { PageService, PageInterface } from '@app/services/page.service';
import {
	CrossFormationReportService,
	GenerateResult,
	GenerateGraph,
} from '@app/services/cross-formation-report.service';
import { Color, ColorInterface } from '@app/models/color';
import { DataAverageInterface } from '@app/abstracts/base-graph/base-graph.component';

export interface DisplayGraph {
	type: string;
	title: string;
	data: DataAverageInterface[];
	colors: Color[];
	maxAxis: number;
	showAverage: boolean;
	// For gauge-svg
	valueAxe1?: number;
	valueAxe2?: number;
	labelMin?: string;
	labelMax?: string;
}

@Component({
	selector: 'app-molecule-cross-formation-report-result',
	templateUrl: './molecule-cross-formation-report-result.component.html',
	styleUrls: ['./molecule-cross-formation-report-result.component.less'],
})
export class MoleculeCrossFormationReportResultComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	protected skeleton: PageSkeletonType = null;

	result: GenerateResult;
	displayGraphs: DisplayGraph[] = [];
	loading = true;
	isPrinting = false;
	printLoading = false;

	/** Free-text comment groups (only present when "Afficher les commentaires" was checked). */
	get comments(): { question: string; answers: string[] }[] {
		return (this.result && this.result.comments) || [];
	}

	get hasComments(): boolean {
		return this.comments.length > 0;
	}

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		private router: Router,
		private http: HttpClient,
		private crossFormationReportService: CrossFormationReportService
	) {
		super(pageService, translate);
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant('cross-formation-report_common_name'),
			breadcrumbs: [
				{
					label: this.translate.instant(
						'cross-formation-report_common_name'
					),
					link: ['/cross-formation-report'],
				},
				{
					label: this.translate.instant(
						'cross-formation-report_generate-btn'
					),
				},
			],
		};
	}

	async ngOnInit() {
		super.ngOnInit();
		this.result = this.crossFormationReportService.generateResult;
		if (!this.result) {
			this.router.navigate(['/cross-formation-report']);
			return;
		}

		let allColors: Color[] = [];
		try {
			const res = await this.http
				.get<any>(
					`${environment.api.uri}/admin/color?_page=0&_limit=50`,
					{
						withCredentials: true,
					}
				)
				.toPromise();
			const items: any[] =
				res && res.items ? res.items : Array.isArray(res) ? res : [];
			allColors = items.map((c: ColorInterface) => new Color(c));
		} catch (e) {
			// Continue without colors
		}

		for (let i = 0; i < this.result.graphs.length; i++) {
			const graphData = this.result.graphs[i];
			const dg = this.buildDisplayGraph(graphData, allColors);
			if (dg) {
				this.displayGraphs.push(dg);
			}
		}

		this.loading = false;
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	private buildDisplayGraph(
		graphData: GenerateGraph,
		allColors: Color[]
	): DisplayGraph {
		const selectedAxes = graphData.computed_axes.filter((a) => a.selected);

		const data: DataAverageInterface[] = selectedAxes.map((a) => ({
			x: a.name,
			y: Math.round((a.totalWeight || 0) * 100) / 100,
		}));

		const colors = allColors.slice(0, Math.max(data.length, 1));

		const dg: DisplayGraph = {
			type: graphData.graph.type,
			title: graphData.graph.name,
			data: data,
			colors: colors,
			maxAxis: graphData.graph.axis_max || 5,
			showAverage: graphData.graph.show_average || false,
		};

		// Gauge: uses gauge-svg with valueAxe1/valueAxe2 and labels
		if (graphData.graph.type === 'gauge' && selectedAxes.length >= 2) {
			dg.valueAxe1 = data[0].y;
			dg.valueAxe2 = data[1].y;
			dg.labelMin = data[0].x;
			dg.labelMax = data[1].x;
		} else if (
			graphData.graph.type === 'gauge' &&
			selectedAxes.length === 1
		) {
			dg.valueAxe1 = data[0].y;
			dg.valueAxe2 = 0;
			dg.labelMin = data[0].x;
			dg.labelMax = '';
		}

		return dg;
	}

	@HostListener('window:afterprint', [])
	onWindowAfterPrint() {
		this.isPrinting = false;
		this.printLoading = false;
	}

	printReport() {
		this.printLoading = true;
		this.isPrinting = true;
		// Wait for ngOnChanges → g2plot to swap renderer to SVG and re-render,
		// then inject viewBox so each SVG can scale to the print page width via CSS.
		setTimeout(() => {
			this.makeSvgsResponsive();
			window.print();
		}, 800);
	}

	private makeSvgsResponsive() {
		const svgs = document.querySelectorAll<SVGSVGElement>(
			'.graph-container svg'
		);
		svgs.forEach((svg) => {
			if (!svg.getAttribute('viewBox')) {
				const rect = svg.getBoundingClientRect();
				const w = parseFloat(svg.getAttribute('width')) || rect.width;
				const h = parseFloat(svg.getAttribute('height')) || rect.height;
				if (!w || !h) return;
				svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
				svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
				svg.removeAttribute('width');
				svg.removeAttribute('height');
			}
			svg.style.setProperty('width', '100%', 'important');
			svg.style.setProperty('height', 'auto', 'important');
			svg.style.setProperty('max-width', '100%', 'important');
			// Clear fixed sizing on g2plot internal wrappers (between svg and .graph-container)
			let parent = svg.parentElement;
			while (parent && !parent.classList.contains('graph-container')) {
				parent.style.setProperty('width', '100%', 'important');
				parent.style.setProperty('max-width', '100%', 'important');
				parent.style.setProperty('min-width', '0', 'important');
				parent.style.setProperty('overflow', 'visible', 'important');
				parent = parent.parentElement;
			}
		});
	}

	goBack() {
		this.router.navigate(['/cross-formation-report']);
	}
}
